import { ApolloClient } from "@apollo/client";
import _ from "lodash";
import { InstalledBaseNodeIotSystemPath } from "../graphql/queries/installedBaseNodeIotSystemPath";
import { InstalledBaseNodeTypeKey } from "../codegen/graphql";
import { isPlcSystemType } from "../config/systemTypes";

export interface IotSystemMatch {
    systemId: string;
    systemTitle: string;
}

interface PathNodeLike {
    nodeType?: { key?: string | null } | null;
    system?: { id?: string | null; title?: string | null; systemType?: string | null } | null;
}

interface NodeWithPath extends PathNodeLike {
    pathNodes?: Array<PathNodeLike | null> | null;
}

// Scan the node itself and its ancestors (`pathNodes` holds parents only) for the
// first PLC system: a SYSTEM-typed node carrying a system with a PLC systemType
// (OTTO_BOX/EWON/ECOTORQUE). Only those hold a user-maintained system-level IoT
// config; NON_PLC and unconfigured (null) systems have nothing to update.
export const findIotSystemInNode = (node?: NodeWithPath | null): IotSystemMatch | null => {
    if (!node) return null;
    const candidates: PathNodeLike[] = [node, ...((node.pathNodes ?? []).filter(Boolean) as PathNodeLike[])];
    for (const candidate of candidates) {
        const isSystem = candidate.nodeType?.key === InstalledBaseNodeTypeKey.SYSTEM;
        const system = candidate.system;
        if (isSystem && isPlcSystemType(system?.systemType) && system?.id) {
            return { systemId: system.id, systemTitle: system.title ?? "" };
        }
    }
    return null;
};

// Resolve the IoT system to offer for update, checking the given node ids in order
// (caller passes the new location first, then the old one) and returning the first
// match. Falsy / duplicate ids are skipped.
export const resolveIotSystemForNodes = async (
    client: ApolloClient<unknown>,
    nodeIds: Array<string | null | undefined>,
): Promise<IotSystemMatch | null> => {
    const orderedIds = nodeIds.filter((id): id is string => !!id).filter((id, i, arr) => arr.indexOf(id) === i);
    for (const id of orderedIds) {
        try {
            const { data } = await client.query({
                query: InstalledBaseNodeIotSystemPath,
                variables: { id },
            });
            const match = findIotSystemInNode(_.get(data, "installedBaseNode"));
            if (match) return match;
        } catch {
            // Treat a failed path lookup as "no IoT system" — the event already
            // succeeded; we just skip the optional prompt.
        }
    }
    return null;
};
